// Phase 1, step 1: transitional wrapper — F1.2 заменил на StorageV2 с миграциями v1→v2.
// История сообщений больше НЕ хранится в localStorage: источник истины — backend,
// который на каждый WS connect присылает credentials-set с полной историей сессии.
import type { StorageV2 } from '../types/internal';

// ── StorageV2 API ──────────────────────────────────────────────────────────────

const STORAGE_KEY_PREFIX = 'cpw:v2:';
const CURRENT_SCHEMA_VERSION = 2 as const;

const DRAFT_TTL_MS = 24 * 60 * 60 * 1000;

// In-memory fallback when localStorage is unavailable (Safari Private, sandboxed iframes, etc.)
const memoryStore = new Map<string, string>();

export const DEFAULT_STORAGE: Omit<StorageV2, 'clientId'> = {
  schemaVersion: CURRENT_SCHEMA_VERSION,
  identity: { consent: false },
  session: { closed: false },
  prefs: {},
};

function storageKeyFor(token: string): string {
  return `${STORAGE_KEY_PREFIX}${token}`;
}

function isStorageAvailable(): boolean {
  try {
    if (typeof localStorage === 'undefined') return false;
    const probe = '__cpw_probe__';
    localStorage.setItem(probe, 'x');
    localStorage.removeItem(probe);
    return true;
  } catch {
    return false;
  }
}

function generateClientIdV2(): number {
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    const buf = new Uint32Array(1);
    crypto.getRandomValues(buf);
    return buf[0] & 0x7fffffff || 1;
  }
  return Math.floor(Math.random() * 0x7fffffff) || 1;
}

function readRaw(token: string): string | null {
  if (isStorageAvailable()) {
    return localStorage.getItem(storageKeyFor(token));
  }
  return memoryStore.get(storageKeyFor(token)) ?? null;
}

function writeRaw(token: string, value: string): void {
  if (isStorageAvailable()) {
    localStorage.setItem(storageKeyFor(token), value); // may throw QuotaExceededError
    return;
  }
  memoryStore.set(storageKeyFor(token), value);
}

function applyDraftTtl(s: StorageV2): StorageV2 {
  const draft = s.prefs.draft;
  if (!draft) return s;
  const fresh = Date.now() - draft.updatedAt < DRAFT_TTL_MS ? draft : undefined;
  if (fresh === draft) return s;
  return { ...s, prefs: { ...s.prefs, draft: fresh } };
}

export function loadStorage(token: string): StorageV2 {
  const raw = readRaw(token);
  if (raw) {
    try {
      const parsed = JSON.parse(raw) as StorageV2;
      if (parsed.schemaVersion === CURRENT_SCHEMA_VERSION) {
        return applyDraftTtl(parsed);
      }
      // Unknown/future schema → fall through to migration check
    } catch {
      /* malformed JSON — fall through */
    }
  }
  const migrated = tryMigrateV1(token);
  if (migrated) {
    saveStorage(token, migrated);
    return migrated;
  }
  const fresh: StorageV2 = { ...DEFAULT_STORAGE, clientId: generateClientIdV2() };
  saveStorage(token, fresh);
  return fresh;
}

function tryMigrateV1(token: string): StorageV2 | null {
  if (!isStorageAvailable()) return null;
  const legacyClientIdRaw = localStorage.getItem('cpw:clientId:' + token);
  const legacyIdentityRaw = localStorage.getItem('cpw:identity:' + token);
  const legacySessionClosedRaw = localStorage.getItem('cpw:session_closed:' + token);
  const hasAnyLegacy = legacyClientIdRaw || legacyIdentityRaw || legacySessionClosedRaw;
  if (!hasAnyLegacy) return null;

  const clientId =
    legacyClientIdRaw && /^\d+$/.test(legacyClientIdRaw)
      ? Number(legacyClientIdRaw)
      : generateClientIdV2();

  let identity: StorageV2['identity'] = { consent: false };
  if (legacyIdentityRaw) {
    try {
      const raw = JSON.parse(legacyIdentityRaw) as Partial<StorageV2['identity']> & {
        email?: string;
      };
      identity = {
        consent: Boolean(raw.consent),
        name: raw.name,
        email: raw.email,
      };
    } catch {
      /* ignore malformed */
    }
  }

  const session: StorageV2['session'] = {
    closed: legacySessionClosedRaw === '1',
  };

  // Cleanup legacy keys (включая legacy messages — они больше не хранятся локально)
  localStorage.removeItem('cpw:clientId:' + token);
  localStorage.removeItem('cpw:messages:' + token + ':' + clientId);
  localStorage.removeItem('cpw:identity:' + token);
  localStorage.removeItem('cpw:session_closed:' + token);

  return {
    schemaVersion: CURRENT_SCHEMA_VERSION,
    clientId,
    identity,
    session,
    prefs: {},
  };
}

export function saveStorage(token: string, data: StorageV2): void {
  try {
    writeRaw(token, JSON.stringify(data));
  } catch {
    /* quota / non-quota errors — silently swallow; storage без messages теперь
       помещается с большим запасом, и retry-логика не нужна. */
  }
}

export function clearStorage(token: string, scope: 'all' | 'session' = 'all'): void {
  if (scope === 'all') {
    if (isStorageAvailable()) localStorage.removeItem(storageKeyFor(token));
    memoryStore.delete(storageKeyFor(token));
    return;
  }
  // scope === 'session' — сбрасываем session.closed; messages больше не в storage.
  const current = loadStorage(token);
  saveStorage(token, {
    ...current,
    session: { closed: false },
  });
}

// ── Debounced scheduleSave ─────────────────────────────────────────────────────

const SAVE_DEBOUNCE_MS = 200;

let pendingTimer: ReturnType<typeof setTimeout> | null = null;
let pendingToken: string | null = null;
let pendingData: StorageV2 | null = null;

export function scheduleSave(token: string, data: StorageV2): void {
  pendingToken = token;
  pendingData = data;
  if (pendingTimer !== null) return;
  pendingTimer = setTimeout(() => {
    flushPendingSave();
  }, SAVE_DEBOUNCE_MS);
}

export function flushPendingSave(): void {
  if (pendingTimer !== null) {
    clearTimeout(pendingTimer);
    pendingTimer = null;
  }
  if (pendingToken !== null && pendingData !== null) {
    saveStorage(pendingToken, pendingData);
  }
  pendingToken = null;
  pendingData = null;
}
